import { useEffect, useMemo, useRef, useState } from "react";
import type { MailAttachment } from "@/lib/api";
import {
  handlePossibleExternalLinkClick,
  stampNewTabAttributes,
} from "@/lib/openExternalLink";
import { htmlTextNewlinesToBreaks, linkifyHtml } from "@/mail/htmlBody";
import { MAIL_LINE_SPACING_EVENT, mailLineSpacingLineHeight, readMailLineSpacing } from "@/mail/prefs";
import {
  attachmentDisplayUrl,
  bindOrphanCidsToImageAttachments,
  buildCidUrlMap,
  findAttachmentByContentId,
  listUnresolvedCids,
  normalizeContentId,
  rewriteCidInHtml,
} from "@/mail/inlineImages";

function visibleTextFromHtml(html: string): string {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return (doc.body?.textContent || "").replace(/\u00a0/g, " ").trim();
  } catch {
    return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }
}

export type MailBodyHtmlProps = {
  html: string;
  /** Message parts used to resolve cid: inline images. */
  attachments?: MailAttachment[];
  /**
   * Fetch a browser-loadable URL for an attachment that has a Content-ID but
   * no url/dataUrl yet (Lightsail attachment endpoint → signed/data URL).
   */
  fetchAttachmentUrl?: (file: MailAttachment) => Promise<string | null | undefined>;
};

/**
 * Renders message HTML inside an open shadow root so email &lt;style&gt; tags
 * cannot paint app chrome (back link, subject, meta) dark-on-dark.
 * Host keeps the light "email card" look; we do not invert email colors.
 * Tip 126: rewrite cid: &lt;img&gt; / CSS urls to attachment download URLs.
 */
export function MailBodyHtml({ html, attachments, fetchAttachmentUrl }: MailBodyHtmlProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const fetchRef = useRef(fetchAttachmentUrl);
  fetchRef.current = fetchAttachmentUrl;
  // Stable key so parent re-renders without new attachment identity don't remount the body.
  const [spacing, setSpacing] = useState(() => readMailLineSpacing());
  const lineHeight = mailLineSpacingLineHeight(spacing);
  useEffect(() => {
    function syncSpacing() {
      setSpacing(readMailLineSpacing());
    }
    window.addEventListener(MAIL_LINE_SPACING_EVENT, syncSpacing);
    return () => window.removeEventListener(MAIL_LINE_SPACING_EVENT, syncSpacing);
  }, []);
  const attachmentKey = useMemo(
    () =>
      (attachments || [])
        .map(
          (file) =>
            `${file.id || ""}|${file.contentId || ""}|${file.url || ""}|${file.downloadUrl || ""}|${file.dataUrl ? "1" : "0"}`,
        )
        .join(";"),
    [attachments],
  );

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const shadow = host.shadowRoot ?? host.attachShadow({ mode: "open" });
    const style = document.createElement("style");
    style.textContent = `
      :host {
        display: block;
        margin-top: 12px;
      }
      .root {
        background: #ffffff;
        color: #111111;
        border-radius: 12px;
        border: 1px solid #d2dbe3;
        padding: 12px 14px;
        line-height: ${lineHeight};
        overflow-x: auto;
        font: 14px/${lineHeight} -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      }
      .root img {
        max-width: 100%;
        height: auto;
      }
      .root a {
        color: #0b57d0;
      }
      .root pre {
        margin: 0;
        white-space: pre-wrap;
        word-break: break-word;
        font: inherit;
      }
      .root p,
      .root div {
        margin: 0;
      }
      .root blockquote {
        margin: 0.35em 0 0;
        padding: 0 0 0 12px;
        border-left: 3px solid #1a73e8;
        color: #3c4043;
      }
      .root .empty {
        margin: 0;
        color: #5f6b76;
      }
    `;
    const root = document.createElement("div");
    root.className = "root";
    const trimmed = htmlTextNewlinesToBreaks((html || "").trim());
    const cidMap = buildCidUrlMap(attachments);
    // Tip 127: first-pass rewrite (contentId + filename keys). Orphan image parts
    // without URLs are resolved async below via filename / order fallback.
    let withInline = trimmed ? rewriteCidInHtml(trimmed, cidMap) : "";
    // Tip 213: Gmail autolinks bare URLs / +phones in the reader; Hub must too.
    if (withInline) withInline = linkifyHtml(withInline);
    const hasMedia = /<(img|video|iframe|svg)\b/i.test(withInline);
    if (!withInline || (!visibleTextFromHtml(withInline) && !hasMedia)) {
      root.innerHTML = `<p class="empty">This message has no body.</p>`;
    } else {
      root.innerHTML = withInline;
    }
    // Tip 140: mail links always new-tab (shadow clicks use composedPath in the
    // document handler; also stamp attributes for middle-click / long-press).
    for (const anchor of Array.from(root.querySelectorAll<HTMLAnchorElement>("a[href]"))) {
      stampNewTabAttributes(anchor);
    }
    root.addEventListener("click", handlePossibleExternalLinkClick, true);
    shadow.replaceChildren(style, root);

    let cancelled = false;
    const unresolved = listUnresolvedCids(withInline);
    const fetchUrl = fetchRef.current;
    const needsAsync =
      Boolean(fetchUrl) &&
      Array.isArray(attachments) &&
      attachments.length > 0 &&
      (unresolved.length > 0 || /<img\b/i.test(withInline));
    if (needsAsync) {
      void (async () => {
        const resolved = new Map<string, string>();
        const byFilename = new Map<string, string>();
        const orphanBind = bindOrphanCidsToImageAttachments(unresolved, attachments, cidMap);
        const urlForFile = async (file: MailAttachment): Promise<string | undefined> => {
          const existing = attachmentDisplayUrl(file);
          if (existing) return existing;
          if (!fetchUrl) return undefined;
          try {
            return (await fetchUrl(file)) || undefined;
          } catch {
            return undefined;
          }
        };

        for (const cid of unresolved) {
          const want = normalizeContentId(cid);
          let file = findAttachmentByContentId(attachments, want);
          if (!file) file = orphanBind.get(want);
          if (!file) continue;
          const href = await urlForFile(file);
          if (href) resolved.set(want, href);
        }

        // Tip 193: also match broken <img alt="Screenshot….png"> to attachment filenames.
        for (const file of attachments) {
          const name = (file.filename || "").trim().toLowerCase();
          if (!name) continue;
          const href = await urlForFile(file);
          if (href) byFilename.set(name, href);
        }

        if (cancelled || (!resolved.size && !byFilename.size) || !hostRef.current) return;
        const liveRoot = hostRef.current.shadowRoot?.querySelector(".root");
        if (!liveRoot) return;
        for (const img of Array.from(liveRoot.querySelectorAll("img"))) {
          const src = img.getAttribute("src") || "";
          if (/^cid:/i.test(src)) {
            const id = normalizeContentId(src);
            let href = resolved.get(id);
            if (!href && id.includes("@")) href = resolved.get(id.slice(0, id.indexOf("@")));
            if (!href) {
              const alt = (img.getAttribute("alt") || img.getAttribute("title") || "")
                .trim()
                .toLowerCase();
              if (alt) href = byFilename.get(alt);
            }
            if (href) img.setAttribute("src", href);
            continue;
          }
          // Empty / invalid src with a filename alt (some clients strip cid: on sync).
          if (!src || src === "about:blank") {
            const alt = (img.getAttribute("alt") || img.getAttribute("title") || "")
              .trim()
              .toLowerCase();
            const href = alt ? byFilename.get(alt) : undefined;
            if (href) img.setAttribute("src", href);
          }
        }
        for (const el of Array.from(liveRoot.querySelectorAll<HTMLElement>("[style*='cid:']"))) {
          const styleAttr = el.getAttribute("style") || "";
          const next = rewriteCidInHtml(styleAttr, resolved);
          if (next && next !== styleAttr) el.setAttribute("style", next);
        }
      })();
    }

    return () => {
      cancelled = true;
    };
    // attachmentKey stands in for attachments identity
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch via ref
  }, [html, attachmentKey, lineHeight]);

  return <div className="mail-body" ref={hostRef} data-mail-body="shadow" />;
}
